import {
  DataGridPro,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  useGridApiRef,
  type GridFilterModel,
  type GridRowId,
  type GridRowParams,
} from '@mui/x-data-grid-pro'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { type Notification } from 'types/modules/notification-center/notification'

import { buildNotificationColumns } from './notificationCenterColumns'
import { GRID_BOTTOM_CLEARANCE } from './notificationCenterConstants'
import { emptyStateFor, fixedFilterFieldFor, viewForKey } from './notificationCenterViews'
import NotificationDetailPanel from './NotificationDetailPanel'
import NotificationEmptyState from './NotificationEmptyState'
import NotificationFeedSkeleton from './NotificationFeedSkeleton'
import NotificationGridToolbar from './NotificationGridToolbar'
import { useNotificationLiveSet } from './useNotificationLiveSet'

const HIDDEN_COLUMNS = Object.freeze({
  [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false,
  title: false,
  body: false,
  area: false,
  category: false,
  read: false,
  status: false,
  occurredAt: false,
})

type NotificationCenterGridProps = {
  notifications: Notification[]
  isLoading: boolean
  query: string
  activeViewKey: string
  expandedRowIds: GridRowId[]
  /** A notification the user asked to read from the bell or a toast: scrolled to once it is in the rows. */
  focusedId?: string | null
  filterModel: GridFilterModel | undefined
  onQueryChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onQueryClear: () => void
  onExpandedRowIdsChange: (ids: GridRowId[]) => void
  onFilterModelChange: (model: GridFilterModel) => void
}

/**
 * Feed of notification cards. A single visible column renders the whole card, the
 * same approach as `AccountActivityAccordion`, while DataGrid still supplies
 * sorting, filtering and detail-panel expansion. No pagination footer: the query
 * walks the endpoint's pages up front, so the feed just scrolls.
 */
const NotificationCenterGrid = ({
  notifications,
  isLoading,
  query,
  activeViewKey,
  expandedRowIds,
  focusedId,
  filterModel,
  onQueryChange,
  onQueryClear,
  onExpandedRowIdsChange,
  onFilterModelChange,
}: NotificationCenterGridProps) => {
  const [filterButtonEl, setFilterButtonEl] = useState<HTMLButtonElement | null>(null)
  const apiRef = useGridApiRef()
  const { areas, views } = useNotificationLiveSet()
  /** Scroll once per id: the rows change on every poll, and re-scrolling would fight the user. */
  const scrolledToRef = useRef<string | null>(null)

  useEffect(() => {
    if (!focusedId || scrolledToRef.current === focusedId) return
    const rowIndex = notifications.findIndex(notification => notification.id === focusedId)
    // The row may not have arrived yet; this runs again when it does.
    if (rowIndex < 0) return
    scrolledToRef.current = focusedId
    apiRef.current?.scrollToIndexes({ rowIndex })
  }, [apiRef, focusedId, notifications])

  const fixedField = fixedFilterFieldFor(activeViewKey)

  // A view pins one dimension itself, so only the panel's own items and the search box count as filtering.
  const isFiltered = Boolean(query.trim()) || (filterModel?.items ?? []).some(item => item.value !== undefined && item.value !== '')
  const emptyState = emptyStateFor({ viewKey: activeViewKey, viewTitle: viewForKey(activeViewKey, views).title, isFiltered })

  const columns = useMemo(() => buildNotificationColumns(fixedField, areas), [fixedField, areas])

  const getRowId = useCallback((row: Notification) => row.id, [])

  const getRowHeight = useCallback(() => 'auto' as const, [])

  const getDetailPanelContent = useCallback(
    (params: GridRowParams<Notification>) => <NotificationDetailPanel notification={params.row} />,
    []
  )

  const getDetailPanelHeight = useCallback(() => 'auto' as const, [])

  const handleExpandedRowIdsChange = useCallback(
    (ids: GridRowId[] | Set<GridRowId>) => onExpandedRowIdsChange(Array.isArray(ids) ? ids : Array.from(ids)),
    [onExpandedRowIdsChange]
  )

  return (
    <DataGridPro
      disableColumnMenu
      disableColumnReorder
      disableColumnResize
      disableColumnSelector
      disableRowSelectionOnClick
      hideFooter
      apiRef={apiRef}
      columnHeaderHeight={0}
      columns={columns}
      columnVisibilityModel={HIDDEN_COLUMNS}
      detailPanelExpandedRowIds={expandedRowIds}
      filterMode="server"
      filterModel={filterModel}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={getDetailPanelHeight}
      getRowHeight={getRowHeight}
      getRowId={getRowId}
      loading={isLoading}
      rows={notifications}
      slotProps={{
        panel: { anchorEl: filterButtonEl },
        toolbar: { query, setFilterButtonEl, onQueryChange, onQueryClear },
        noRowsOverlay: emptyState,
      }}
      slots={{
        toolbar: NotificationGridToolbar,
        loadingOverlay: NotificationFeedSkeleton,
        noRowsOverlay: NotificationEmptyState,
      }}
      sx={{
        border: 'none',
        // The card draws its own border and tint, so the grid chrome gets out of the way.
        '.MuiDataGrid-cell': { border: 'none', px: 0, py: 0 },
        '.MuiDataGrid-row:hover': { bgcolor: 'transparent' },
        '.MuiDataGrid-row.Mui-selected': { bgcolor: 'transparent' },
        '.MuiDataGrid-detailPanel': { bgcolor: 'transparent' },
        '.MuiDataGrid-columnHeaders': { display: 'none' },
        // Bottom padding belongs to the scrollable area so the last card clears the
        // floating Intercom launcher without leaving a dead gap under the grid.
        '.MuiDataGrid-virtualScroller': { pb: GRID_BOTTOM_CLEARANCE },
      }}
      onDetailPanelExpandedRowIdsChange={handleExpandedRowIdsChange}
      onFilterModelChange={onFilterModelChange}
    />
  )
}

export default NotificationCenterGrid
